import { Translation } from '@/components/foundation/Translation'
import { Button } from '@/components/ui/Button'
import { LinePicto } from '@/components/ui/LinePicto'
import { useDataAllLines } from '@/hooks/data/useDataAllLines'
import { useDataConnectionsByArea } from '@/hooks/data/useDataConnectionsByArea'
import { useModules } from '@/hooks/useModule'
import { useTranslator } from '@/hooks/useTranslator'
import { getArea } from '@/stores/areas'
import { getConfigApiKeys } from '@/stores/config'
import { useDispatch, useSelector } from '@/stores/hooks'
import { getStop } from '@/stores/stops'
import { setHoveredLine, type PopupInfo } from '@/stores/temp'
import { determineTypeStop } from '@/util/journeys'
import type { Area, Connection } from '@common/types/Area'
import type { ApiKeys } from '@common/types/config/Api'
import { isFullLine, type Line, type PartialLine } from '@common/types/Line'
import type { Stop } from '@common/types/Stop'
import type { Point } from 'geojson'
import { useState } from 'react'
import { Link } from 'wouter'
import StreetViewModal from '../StreetViewModal'
import styles from './PopupContentStop.scss'

function DisplayContent({
	stop,
	areaId,
	isStop,
}: {
	stop: Stop | Area
	areaId: string
	isStop: boolean
}) {
	return (
		<>
			<DisplayLines
				elements={stop.lines}
				stopId={stop.id}
				areaId={areaId}
				type={isStop ? 'stop' : 'area'}
			/>
			{isStop && (
				<DisplayStreetViewBtn stopGeojson={stop.geojson as Point} />
			)}
		</>
	)
}

export function DisplayLines({
	elements,
	stopId,
	areaId,
	type,
}: {
	elements: string[] | null
	stopId: string
	areaId: string
	type: 'stop' | 'area'
}) {
	const { lines } = useDataAllLines()
	const { connectionsByArea } = useDataConnectionsByArea(areaId)

	return (
		<div className={styles.gridLinesContainer}>
			{elements && (
				<Translation
					html
					as="p"
					id={'popup:stop:label-lines-through'}
					lines={elements.length}
				/>
			)}
			<div className={styles.gridLines}>
				{elements?.map(element => {
					const findLine = Object.values(lines).find(
						l => l.id === element,
					)
					const connections = connectionsByArea?.filter(
						connection => connection.line === findLine?.id,
					)
					return (
						findLine && (
							<DisplayLine
								line={findLine}
								stopId={type === 'area' ? null : stopId}
								connections={connections ?? []}
							/>
						)
					)
				})}
			</div>
		</div>
	)
}

function DisplayLine({
	line,
	stopId,
	connections,
}: {
	line: Line | PartialLine
	stopId: string | null
	connections: Connection[]
}) {
	const modules = useModules()
	const dispatch = useDispatch()

	const module = modules.find(mod => mod.module === 'lines')
	let direction: 'forward' | 'backward' | undefined

	if (!stopId) {
		direction = (['forward', 'backward'] as const).find(dir =>
			connections.some(connection => connection.direction === dir),
		)
	} else {
		direction = (['forward', 'backward'] as const).find(dir =>
			connections.some(
				connection =>
					connection.direction === dir && stopId === connection.stop,
			),
		)
	}

	if (!direction && isFullLine(line)) {
		direction = (['forward', 'backward'] as const).find(dir =>
			line.routes.some(route => route.direction === dir),
		)
	}

	if (!direction) direction = 'forward'

	if (!module) return null

	const href = stopId
		? `${module.path}/${line.id}/${direction}/${stopId}`
		: `${module.path}/${line.id}/${direction}`

	return (
		<Link
			href={href}
			aria-label={line.code}
			onMouseEnter={() => dispatch(setHoveredLine(line.id))}
			onMouseLeave={() => {
				dispatch(setHoveredLine(null))
			}}
		>
			<LinePicto
				key={line.id}
				line={line}
				className={styles.buttonLinePicto}
				aria-hidden="true"
			/>
		</Link>
	)
}

export function DisplayStreetViewBtn({ stopGeojson }: { stopGeojson: Point }) {
	const apiKeys = useSelector(getConfigApiKeys)
	const [showModalStreetView, setShowModalStreetView] = useState(false)
	const translator = useTranslator()

	const handleClickStreetViewBtn = () => {
		setShowModalStreetView(prev => !prev)
	}

	const isStreetView =
		stopGeojson.coordinates[1] != null &&
		stopGeojson.coordinates[0] != null &&
		Object.keys(apiKeys as ApiKeys).includes('streetview')

	if (!isStreetView) return null

	return (
		<>
			<div className={styles.streetViewContainer}>
				<Button
					className={styles.buttonStreetView}
					level="popup"
					iconUrl="assets/StreetView.svg"
					iconClassName={styles.iconStreetView}
					onClick={handleClickStreetViewBtn}
					label={translator.translate('streetview:button-display')}
					alignLeft
				/>
				{showModalStreetView && (
					<DisplayStreetViewModal
						handleClickStreetViewBtn={handleClickStreetViewBtn}
						stopGeojson={stopGeojson}
					/>
				)}
			</div>
		</>
	)
}

export function DisplayStreetViewModal({
	handleClickStreetViewBtn,
	stopGeojson,
}: {
	handleClickStreetViewBtn: () => void
	stopGeojson: Point
}) {
	const apiKeys = useSelector(getConfigApiKeys)

	const isStreetView =
		stopGeojson.coordinates[1] != null &&
		stopGeojson.coordinates[0] != null &&
		Object.keys(apiKeys as ApiKeys).includes('streetview')

	if (isStreetView && apiKeys) {
		return (
			<StreetViewModal
				onClose={handleClickStreetViewBtn}
				apiKey={apiKeys.streetview}
				lat={stopGeojson.coordinates[1]}
				lon={stopGeojson.coordinates[0]}
			/>
		)
	} else {
		return null
	}
}

export function PopupContentStop({ id, type }: PopupInfo) {
	const stop = useSelector(state => {
		if (!id || !type) return null
		if (type === 'stop_point') return getStop(state, id)
		if (type === 'stop_area') return getArea(state, id)
		return null
	})

	const areaId = stop && determineTypeStop(stop) ? stop.stopArea : stop?.id

	if (!areaId) return null
	if (!stop) return null
	const isStop = determineTypeStop(stop)
	if (stop.geojson.type !== 'Point') return null

	return (
		<div className={styles.content}>
			<DisplayContent stop={stop} areaId={areaId} isStop={isStop} />
		</div>
	)
}
